import type { GraphQLError } from 'graphql';
import { DEFAULT_BACKGROUND_CONFIG } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { NOT_APPLICABLE } from '../../constants.tsx';
import type {
	NavigationProject,
	NavigationOverview,
	NavigationResourceType,
} from '../../types.tsx';
import {
	OVERVIEWS_SUCCESS_RESPONSE_TYPE,
	type NavigationQueryJiraProjectWithPermissions,
	type NavigationQueryJiraWorkManagementOverview,
	type NavigationNodeResponse,
	type OverviewsSuccess,
	type OverviewsError,
} from './types.tsx';

const SIMPLIFIED_PROJECT_STYLE = 'TEAM_MANAGED_PROJECT';

const PREMIUM = 'PREMIUM';

const mapProjectNode = (node: NavigationQueryJiraProjectWithPermissions): NavigationProject => ({
	key: node.key,
	id: Number(node.projectId),
	avatarUrl: node.avatar?.large ?? '',
	name: node.name,
	type: node.projectType.toLowerCase(),
	isSimplified: node.projectStyle === SIMPLIFIED_PROJECT_STYLE,
	isFavorite: node.isFavourite,
	permissions: {
		viewIssues: node.viewIssues.canPerform,
	},
	defaultView: node.defaultView?.typeKey?.toLowerCase(),
});

const mapOverviewNode = (node: NavigationQueryJiraWorkManagementOverview): NavigationOverview => ({
	id: node.id,
	name: node.name,
	theme: node?.theme ?? DEFAULT_BACKGROUND_CONFIG.name,
});

export const transformData = (
	navigation: NavigationNodeResponse | null,
	areOverviewsGone: boolean,
): NavigationResourceType => {
	if (navigation == null) {
		// not a JWM context
		return NOT_APPLICABLE;
	}

	let overviews: NavigationOverview[] = [];
	let overviewsError: GraphQLError | null = null;

	const isPremiumUserSeat = navigation.jwmLicensing?.currentUserSeatEdition === PREMIUM;

	if (isPremiumUserSeat) {
		if (navigation.overviews?.__typename === OVERVIEWS_SUCCESS_RESPONSE_TYPE) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			overviews = (navigation.overviews as OverviewsSuccess).edges.map((edge) =>
				mapOverviewNode(edge.node),
			);
		} else {
			overviewsError =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(navigation.overviews as OverviewsError) ?? new Error('Unknown overviews error');
		}
	}

	let recents = navigation.recentProjects?.edges?.map((edge) => mapProjectNode(edge.node)) ?? [];
	const favorites =
		navigation.favoriteProjects?.edges?.map((edge) => mapProjectNode(edge.node)) ?? [];

	// Remove favorited projects from the recent projects list
	recents = recents.filter(
		(recentProject) => !favorites.some((favProject) => favProject.id === recentProject.id),
	);

	return {
		recents,
		favorites,
		hasMoreFavorite: navigation.favoriteProjects?.pageInfo?.hasNextPage ?? false,
		overviews,
		overviewsError,
		isPremiumUserSeat,
		shouldHideOverviews: areOverviewsGone,
	};
};
